import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { usePagePermissions } from '../hooks/usePagePermissions';
import {
  useDraftsList, useApproveDraft, useRejectDraft, useBulkApproveDrafts, useUpdateDraft,
  useUpdateDraftAttachments, useArchiveSearch, useReopenDraft, type AttachmentAction,
} from '../hooks/useDraftsApi';
import { useFailedDistributions } from '../hooks/useReportsApi';
import { useToast } from '../components/ui/Toast';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import {
  DRAFT_STATUS_LABELS, DRAFT_TYPE_LABELS, DRAFT_WARNING_LABELS, DUPLICATE_LABELS, labelFor, tooltipFor,
} from '../lib/labels';
import { downloadCsv, type CsvColumn } from '../lib/csv';
import type { DraftAttachmentRef, DraftWarning, FailedDistributionRow, OutboundDraft } from '../types/domain';

import { Button, TEXT_LINK } from '../components/ui/Button';
import { FOCUS_RING, FOCUS_RING_TIGHT } from '../components/ui/focus';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Dialog, DialogCancel } from '../components/ui/Dialog';
import { Field, Input, Textarea } from '../components/ui/Field';
import { LoadError } from '../components/ui/LoadError';
import { SkeletonTable, LoadingAnnouncement } from '../components/ui/Skeleton';
import { isoDateTime } from '../lib/dates';

/** The six columns beside the select box, in one place — the skeleton and the
 *  real header read the same list (P38, task 5). */
const DRAFT_HEADERS = ['Type', 'Tickets', 'Customer / Product', 'To', 'Subject', 'Created'];
/**
 * Everything the server says has to be read before this mail goes out.
 *
 * The sentences come from `api/src/functions/drafts/warnings.ts` — already
 * written for a person and naming the actual file, language and product of THIS
 * draft. The screen supplies the chip and the "what do I do" tooltip and adds
 * no prose of its own, so the approval queue, the attachment endpoint and bulk
 * approval cannot describe the same problem three different ways.
 *
 * Blocking warnings are red and keep the draft out of a batch; advisory ones
 * are amber and state a fact (a signed C9 override, a recipient the allowlist
 * will hold back) without stopping anybody.
 */
export function DraftWarnings({ warnings }: { warnings: DraftWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="mb-3 space-y-2">
      {warnings.map((w, i) => (
        <div
          key={`${w.code}-${i}`}
          className={`rounded border px-2.5 py-2 text-xs ${w.blocking
            ? 'border-red-300 bg-red-50 text-red-900'
            : 'border-amber-300 bg-amber-50 text-amber-900'}`}
        >
          <strong title={tooltipFor(DRAFT_WARNING_LABELS, w.code)}>
            {labelFor(DRAFT_WARNING_LABELS, w.code)}
          </strong>{' '}
          {w.message}
        </div>
      ))}
    </div>
  );
}

function DocumentLine({ refItem }: { refItem: DraftAttachmentRef }) {
  return (
    <span className="text-xs text-gray-600">
      Revision{' '}
      {refItem.revisionDate
        ? <span className="font-medium text-gray-900">{refItem.revisionDate}</span>
        : <span className="font-medium text-amber-700">not stated in the file name — check the document itself</span>}
      {refItem.language ? ` · language ${refItem.language}` : ' · language not stated in the file name'}
      {refItem.countryTokens.length > 0 ? ` · ${refItem.countryTokens.join('/')} edition` : ''}
      {refItem.origin === 'ArchiveSearch' && refItem.addedBy ? ` · added from the archive by ${refItem.addedBy}` : ''}
    </span>
  );
}

/**
 * Search the official archive and attach what you find (P24, decision 5).
 *
 * There is no file picker and there will not be one in the pilot: every
 * document a customer receives comes out of
 * `ORG-Regulatory > General – Compliance Docs`, which the Group archiving
 * procedure names as the exclusive archive. Upload is on the Phase-3 list.
 *
 * The query the person typed travels with the attach call, because the server
 * re-runs it and takes the fetch ids from ITS result rather than from anything
 * the browser sends.
 */
function ArchivePicker({
  draft, language, onAttach, pending,
}: {
  draft: OutboundDraft;
  language: string | null;
  onAttach: (query: string, fileName: string) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [submitted, setSubmitted] = useState('');
  const { data, isFetching } = useArchiveSearch(submitted, language, open && submitted.length >= 2);

  if (!open) {
    return (
      <Button variant="quiet" size="sm" className="-ml-2.5 mt-3" onClick={() => setOpen(true)}>
        Search the archive for another document
      </Button>
    );
  }

  return (
    <div className="mt-3 rounded border border-blue-200 bg-blue-50/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Official archive{language ? ` · ranking ${language} first` : ''}
      </div>
      <form
        className="mt-2 flex gap-2"
        onSubmit={(e) => { e.preventDefault(); setSubmitted(term.trim()); }}
      >
        <Input
          value={term}
          onChange={e => setTerm(e.target.value)}
          placeholder={draft.productName ?? 'product name'}
          aria-label="Search the official archive"
        />
        <Button type="submit" variant="secondary" size="sm" disabled={term.trim().length < 2}>
          Search
        </Button>
        <Button type="button" variant="quiet" size="sm" onClick={() => setOpen(false)}>
          Close
        </Button>
      </form>

      {isFetching && <div className="mt-2 text-xs text-gray-500">Searching…</div>}
      {!isFetching && data && data.candidates.length === 0 && (
        <div className="mt-2 text-xs text-gray-500">
          Nothing in the archive matched that. {data.explanation}
        </div>
      )}
      {!isFetching && data && data.candidates.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {data.candidates.map(c => (
            <li key={`${c.rank}-${c.fileName}`} className="flex items-start justify-between gap-3 text-xs">
              <span className="min-w-0">
                {c.webUrl
                  ? <a href={c.webUrl} target="_blank" rel="noopener noreferrer" className={TEXT_LINK}>{c.fileName}</a>
                  : c.fileName}
                <span className="block text-gray-500">{c.rankReason}</span>
              </span>
              <button
                className="shrink-0 rounded-md border px-2 py-1 font-medium text-gray-700 hover:bg-white disabled:opacity-50"
                disabled={pending}
                onClick={() => onAttach(submitted, c.fileName)}
              >
                Attach
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The documents a draft proposes, and what the approver has to check.
 *
 * Two things must be visible here and nowhere else, both from
 * `context/reports/2026-08-24_sds-handling-katia.md`:
 *
 *  - **The revision date (C8).** `1_SDS` is a filing convention, not a currency
 *    guarantee — a superseded sheet nobody moved still sits in the folder.
 *    Regulatory's compensating control today is reading the date, so the date
 *    parsed from the file name has to reach this screen.
 *  - **Whether the file name actually names the requested product.** Measured
 *    2026-08-25 over the 11 real fixture tickets: for 10 of them the top hit is
 *    a DIFFERENT product's data sheet, because semantic search matches the
 *    family name. Since P24 that judgement is made on the server (`warnings.ts`)
 *    and rendered by `DraftWarnings`, so the batch cannot disagree with the
 *    screen — this panel no longer reimplements the product test.
 *
 * **The alternatives are now actionable (P24).** For six weeks this panel ended
 * with the comment "Choosing a different one is not yet wired — today the move
 * is to reject the draft with a note", and `DRA-2026-00045` is what that cost:
 * `RUETASOLV_DI_2025-02-13_IT.pdf` was visible at rank 3 while a different
 * chemical's sheet went to Mapei. Switching, removing and adding from the
 * archive are three calls to one endpoint, and every change recomputes the
 * warnings server-side — a language switch always needs a fresh signature,
 * because the previous one belonged to the document that was there before.
 */
export function DocumentPanel({ draft, canEdit }: { draft: OutboundDraft; canEdit: boolean }) {
  const { toast } = useToast();
  const attachments = useUpdateDraftAttachments();
  const [confirm, setConfirm] = useState<{ action: AttachmentAction; message: string } | null>(null);

  const refs = (draft.attachmentRefs ?? []) as DraftAttachmentRef[];
  const editable = canEdit && draft.status === 'PendingApproval';
  const chosen = refs.find(r => r.chosen);
  const alternatives = refs.filter(r => !r.chosen);
  // What the request is owed, as far as the browser can tell. Only a hint for
  // ranking the archive search — the server decides, including the market
  // fallback the browser has no data for.
  const owedLanguage = chosen?.requestedLanguage ?? null;

  function run(action: AttachmentAction, successText: string) {
    attachments.mutate(action, {
      onSuccess: () => toast(successText, 'success'),
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Could not change the attachment';
        // A 409 here is the C9 guard: the document that would now go out is not
        // in the language this request is owed and nobody has signed for it.
        // The server's own sentence is the confirmation prompt — it names the
        // owed language, the file and the rule.
        if (/acceptLanguageMismatch/.test(message)) {
          setConfirm({ action: { ...action, acceptLanguageMismatch: true }, message });
          return;
        }
        toast(message, 'error');
      },
    });
  }

  return (
    <div className="mt-3 rounded border border-gray-200 bg-white p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Document to attach</div>

      {!chosen && (
        <div className="mt-1 text-xs text-gray-500">No document is attached to this reply.</div>
      )}

      {chosen && (
        <div className="mt-1">
          <div className="text-sm font-medium text-gray-900">
            {chosen.webUrl ? (
              <a href={chosen.webUrl} target="_blank" rel="noopener noreferrer" className={TEXT_LINK}>
                {chosen.fileName}
              </a>
            ) : (
              chosen.fileName
            )}
          </div>
          <div className="mt-0.5"><DocumentLine refItem={chosen} /></div>
          {editable && (
            <button
              className={`mt-2 text-xs text-err hover:underline disabled:opacity-50 ${FOCUS_RING}`}
              disabled={attachments.isPending}
              onClick={() => run({ id: draft.id, action: 'remove' }, 'Document removed from the draft')}
            >
              Remove this document
            </button>
          )}
        </div>
      )}

      {alternatives.length > 0 && (
        <details className="mt-3" open={editable && !chosen}>
          <summary className="cursor-pointer text-xs text-gray-500">
            {alternatives.length} other candidate{alternatives.length === 1 ? '' : 's'} the search found
          </summary>
          <ul className="mt-2 space-y-1.5">
            {alternatives.map(a => (
              <li key={a.rank} className="flex items-start justify-between gap-3 text-xs text-gray-600">
                <span className="min-w-0">
                  <span className="text-muted">{a.rank}.</span>{' '}
                  {a.webUrl ? (
                    <a href={a.webUrl} target="_blank" rel="noopener noreferrer" className={TEXT_LINK}>
                      {a.fileName}
                    </a>
                  ) : (
                    a.fileName
                  )}
                  <span className="text-muted">
                    {' '}— {a.revisionDate ?? 'no revision date'}{a.language ? `, ${a.language}` : ''}
                  </span>
                </span>
                {editable && (
                  <button
                    className="shrink-0 rounded-md border px-2 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    disabled={attachments.isPending}
                    onClick={() => run(
                      { id: draft.id, action: 'choose', rank: a.rank },
                      `${a.fileName} is now the document on this draft`,
                    )}
                  >
                    Use this one
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {editable && (
        <ArchivePicker
          draft={draft}
          language={owedLanguage}
          pending={attachments.isPending}
          onAttach={(query, fileName) => run(
            { id: draft.id, action: 'add', query, fileName, language: owedLanguage },
            `${fileName} attached from the archive`,
          )}
        />
      )}

      {confirm && (
        <ConfirmModal
          title="Deliver a document in the wrong language?"
          message={confirm.message}
          confirmLabel="Yes — deliver this one anyway"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => {
            const action = confirm.action;
            setConfirm(null);
            run(action, 'Document changed — the language override is recorded with your name');
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/**
 * Edit a draft before it is approved.
 *
 * `PUT /api/v1/drafts/{id}` and `useUpdateDraft` have existed since P7 and were
 * called by nothing at all — so until P14 the only way to change one word of an
 * AI-composed mail was to reject it and compose again. That is the whole reason
 * three of the four editable fields matter: **recipients**, because a relayed
 * request can resolve to the wrong address; **subject** and **body**, because
 * Regulatory owns the wording and the model only fills two short strings into
 * a template.
 *
 * `EditedBeforeSend` is set by the endpoint, not here, and it is not
 * cosmetic — it is the record that a human changed the machine's text, and it
 * rides along into the `DraftSent` audit row and onto the delivery proof.
 *
 * Editable ONLY while the draft is `PendingApproval`: the endpoint's UPDATE
 * carries `AND Status = 'PendingApproval'` and answers 404 otherwise, which is
 * the same concurrency guard approve and reject use. So an edit cannot land on
 * a draft somebody approved a second earlier.
 *
 * The body is edited as raw HTML on purpose. It IS an HTML mail, a rich-text
 * editor would silently rewrite the template's markup, and the audience is a
 * regulatory specialist editing a paragraph — not authoring a page.
 *
 * Since P24 the SERVER also refuses a save that would reintroduce a
 * `{{placeholder}}` or an unusable address, and reports back when the
 * recipients are outside the pilot's allowed domains. The endpoint's message is
 * shown verbatim: it names the placeholder or the address.
 */
export function DraftEditor({ draft }: { draft: OutboundDraft }) {
  const { toast } = useToast();
  const update = useUpdateDraft();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(draft.subject);
  const [bodyHtml, setBodyHtml] = useState(draft.bodyHtml);
  const [to, setTo] = useState(draft.toAddresses.join(', '));
  const [cc, setCc] = useState((draft.ccAddresses ?? []).join(', '));

  const addresses = (text: string) => text.split(',').map(a => a.trim()).filter(Boolean);
  const toList = addresses(to);
  const dirty =
    subject !== draft.subject ||
    bodyHtml !== draft.bodyHtml ||
    toList.join(', ') !== draft.toAddresses.join(', ') ||
    addresses(cc).join(', ') !== (draft.ccAddresses ?? []).join(', ');

  if (draft.status !== 'PendingApproval') return null;

  if (!open) {
    return (
      <Button variant="quiet" size="sm" className="-ml-2.5" onClick={() => setOpen(true)}>
        Edit before approving
      </Button>
    );
  }

  return (
    <div className="mt-3 rounded border border-blue-200 bg-blue-50/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Edit this draft</div>

      {/* P40: four labels that were not wired to anything and four controls with no
          focus ring (audit S2-20 and S2-11). `Field` generates the id, so both are
          fixed by construction rather than by remembering. This is the form that
          edits a mail going to a customer - the last place to leave a label that
          does not focus its field. */}
      <Field label="To" hint="Comma-separated." className="mt-2">
        {(a) => <Input {...a} value={to} onChange={e => setTo(e.target.value)} />}
      </Field>

      <Field label="Cc" hint="Comma-separated, may be empty." className="mt-2">
        {(a) => <Input {...a} value={cc} onChange={e => setCc(e.target.value)} />}
      </Field>

      <Field label="Subject" className="mt-2">
        {(a) => <Input {...a} value={subject} onChange={e => setSubject(e.target.value)} />}
      </Field>

      <Field label="Body (HTML)" className="mt-2">
        {(a) => (
          <Textarea
            {...a}
            rows={10}
            value={bodyHtml}
            onChange={e => setBodyHtml(e.target.value)}
            className="font-mono text-xs"
          />
        )}
      </Field>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="primary" size="sm"
          disabled={!dirty || toList.length === 0 || update.isPending}
          onClick={() => update.mutate(
            {
              id: draft.id,
              subject,
              bodyHtml,
              toAddresses: toList,
              ccAddresses: addresses(cc).length > 0 ? addresses(cc) : null,
            },
            {
              onSuccess: () => { toast('Draft saved — marked as edited before sending', 'success'); setOpen(false); },
              // The endpoint refuses a placeholder or an unusable address and
              // says which one. Showing "could not save" instead would leave the
              // person guessing at their own typo.
              onError: (err: unknown) => toast(
                err instanceof Error ? err.message : 'Could not save — the draft may already have been decided',
                'error',
              ),
            },
          )}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button variant="secondary" size="sm"
          onClick={() => {
            setSubject(draft.subject); setBodyHtml(draft.bodyHtml);
            setTo(draft.toAddresses.join(', ')); setCc((draft.ccAddresses ?? []).join(', '));
            setOpen(false);
          }}
        >
          Cancel
        </Button>
        {toList.length === 0 && <span className="text-xs text-red-700">A reply needs at least one recipient.</span>}
        {draft.editedBeforeSend && (
          <span className="text-xs text-gray-500">This draft has already been edited by a person.</span>
        )}
      </div>
    </div>
  );
}

/**
 * Two draft ids, compared as text (P37a).
 *
 * THE ID ON THE WIRE IS A STRING, ALTHOUGH THE DTO SAYS `number`.
 * `OutboundDrafts.Id` is a BIGINT and tedious hands those back as JS strings;
 * `mapDraft` passes `r.Id` straight through, so `d.id` is `"50"` at runtime.
 * Every comparison on this screen has always been `d.id` against `d.id`, so it
 * never showed — and the first thing to compare it with a real number was the
 * `?draft=` deep link, which then silently opened nothing. Measured, not
 * reasoned: the live endpoint answers `{"id": "31"}`.
 *
 * Compared as text rather than cast away, and written down: the DTO's declared
 * type is wrong for every draft id in the SPA, and correcting THAT belongs to
 * whoever owns `drafts/mappers.ts`.
 */
export function sameDraft(a: number | string | null | undefined, b: number | string | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
}

/**
 * Single-open master-detail accordion, same pattern as reporting_app's
 * PoCheckTab: click a row, a sibling <tr> expands below it.
 */
export default function ApprovalQueue() {
  const { canEdit } = usePagePermissions('/approvals');
  const { toast } = useToast();
  const { data: drafts, isLoading, isError, error, errorUpdatedAt, refetch } = useDraftsList();
  /**
   * P37a — `?draft=<id>`, the deep link the ticket screen's Reply card carries.
   *
   * A ticket used to have no way to point at its own reply: the drafts waiting
   * here are only the `PendingApproval` ones, and an APPROVED draft the
   * recipient policy held lives in *Failed distributions*, a panel that is
   * collapsed by default. Five drafts are in that state, one of them real, and
   * they were reachable only by somebody who knew to look inside a collapsed
   * box. So the parameter opens whichever of the two holds the draft.
   *
   * It is read once, into state, rather than driving the accordion directly:
   * closing the row again has to be possible without the URL reopening it.
   */
  const [searchParams] = useSearchParams();
  const linkedDraftId = Number(searchParams.get('draft')) || null;
  const [expandedId, setExpandedId] = useState<number | null>(linkedDraftId);
  const isExpanded = (id: number | string) => sameDraft(expandedId, id);
  const [rejectTarget, setRejectTarget] = useState<OutboundDraft | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [selected, setSelected] = useState<number[]>([]);

  const approve = useApproveDraft();
  const reject = useRejectDraft();
  const bulkApprove = useBulkApproveDrafts();

  const pending = drafts ?? [];
  // A draft carrying a blocking warning is never part of a batch — the server
  // holds it back regardless (`NeedsIndividualReview`), and offering the
  // checkbox anyway would promise something the endpoint then refuses.
  const batchable = pending.filter(d => d.bulkApprovable);
  const allSelected = batchable.length > 0 && selected.length === batchable.length;

  function toggle(id: number) {
    setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  }

  /**
   * Approve everything currently ticked.
   *
   * The endpoint answers 200 even when individual sends failed, so the outcome
   * is read out of the summary rather than inferred from the promise resolving.
   * Saying "12 approved" when three of them failed would be the worst possible
   * lie for this particular screen.
   */
  function approveSelected() {
    bulkApprove.mutate(
      { draftIds: selected },
      {
        onSuccess: (result) => {
          const { sent, skipped, failed, notPending, needsIndividualReview } = result.summary;
          const parts = [
            sent > 0 ? `${sent} sent` : null,
            skipped > 0 ? `${skipped} approved but not sent (sending is off)` : null,
            notPending > 0 ? `${notPending} already decided by someone else` : null,
            needsIndividualReview > 0
              ? `${needsIndividualReview} held back — open each one, it carries a warning`
              : null,
            failed > 0 ? `${failed} FAILED` : null,
          ].filter(Boolean);
          toast(parts.join(' · ') || 'Nothing to do', failed > 0 || needsIndividualReview > 0 ? 'error' : 'success');
          setSelected([]);
        },
        onError: () => toast('Bulk approval could not be submitted', 'error'),
      }
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink">Approvals</h1>
      <p className="mt-1 text-sm text-gray-500">
        Every outbound mail is drafted first — nothing sends without a human approving it here.
      </p>

      <FailedDistributionsPanel canEdit={canEdit} highlightDraftId={linkedDraftId} />

      {isError && <LoadError what="Drafts" error={error} at={errorUpdatedAt} onRetry={refetch} />}
      {isLoading && (
        <>
          <LoadingAnnouncement what="drafts waiting for approval" />
          <SkeletonTable headers={canEdit ? ['', ...DRAFT_HEADERS] : DRAFT_HEADERS} rows={6} />
        </>
      )}

      {!isLoading && !isError && canEdit && selected.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
          <span className="text-sm text-green-900">
            <strong>{selected.length}</strong> draft{selected.length === 1 ? '' : 's'} selected
          </span>
          {/* P40: `bg-green-600` was STOCK green, not `--brand-600`. These two are the
              most important primaries in the app - the buttons that release a mail to a
              customer - and they were the last two drawn in a colour the token layer
              does not carry. The kit also gives them the measured disabled appearance
              (`--head` ground, `--faint` text, opacity 1) instead of `opacity-50`. */}
          <Button
            disabled={bulkApprove.isPending}
            onClick={approveSelected}
          >
            {bulkApprove.isPending ? 'Approving…' : `Approve ${selected.length} selected`}
          </Button>
          <button className={`text-sm ${TEXT_LINK}`} onClick={() => setSelected([])}>
            Clear
          </button>
          <span className="text-xs text-green-900/70">
            Each one is sent individually; a failure stops none of the others.
          </span>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500">
                {canEdit && (
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label="Select all drafts"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? [] : batchable.map(d => d.id))}
                    />
                  </th>
                )}
                {DRAFT_HEADERS.map(h => <th key={h} className="px-3 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {(drafts ?? []).map(d => (
                <Fragment key={d.id}>
                  <tr
                    // P37, audit S1-01: the row keeps its click as a convenience but
                    // stops claiming to be a control. The control is the button in the
                    // Type cell, which is what a keyboard reaches and what says
                    // `aria-expanded`.
                    className="border-t border-gray-100 hover:bg-gray-50"
                    onClick={() => setExpandedId(isExpanded(d.id) ? null : d.id)}
                  >
                    {canEdit && (
                      <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select draft ${d.id}`}
                          checked={selected.includes(d.id)}
                          disabled={!d.bulkApprovable}
                          title={d.bulkApprovable
                            ? undefined
                            : 'This draft carries a warning that has to be read on the draft itself — open it and approve it there.'}
                          onChange={() => toggle(d.id)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      {/* The row's one real control (P37). It names the draft, states
                          whether the detail is open, and points at the row that holds
                          it — so the keyboard gets what the mouse always had. */}
                      <button
                        type="button"
                        aria-expanded={isExpanded(d.id)}
                        aria-controls={`draft-detail-${d.id}`}
                        onClick={e => { e.stopPropagation(); setExpandedId(isExpanded(d.id) ? null : d.id); }}
                        title={`${tooltipFor(DRAFT_TYPE_LABELS, d.draftType)}

Code: ${d.draftType}

Opens the draft below.`}
                        className={`inline-flex h-8 items-center gap-1 rounded-ctl text-left hover:underline ${FOCUS_RING_TIGHT}`}
                      >
                        {labelFor(DRAFT_TYPE_LABELS, d.draftType)}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      {d.ticketNumbers.join(', ') || '—'}
                      {/* P27. The last screen before a mail leaves the building is
                          where "a second ticket asks for this too" has to be said:
                          approving here answers ONE of them, and the other stays
                          open. Deliberately not a blocking warning — the reply is
                          correct for the ticket it belongs to. */}
                      {(d.siblingTicketNumbers ?? []).length > 0 && (
                        <Badge
                          tone="info"
                          className="ml-1.5"
                          title={`${tooltipFor(DUPLICATE_LABELS, 'duplicateFound')}

Also open: ${(d.siblingTicketNumbers ?? []).join(', ')}`}
                        >
                          also {(d.siblingTicketNumbers ?? []).join(', ')}
                        </Badge>
                      )}
                    </td>
                    <td className="truncate px-3 py-2 text-gray-600">
                      {d.customerName ?? '—'}{d.productName ? ` / ${d.productName}` : ''}
                    </td>
                    <td className="truncate px-3 py-2">{d.toAddresses.join(', ')}</td>
                    <td className="px-3 py-2">
                      {/*
                        * P38, task 4 — audit S2-06/S1 "a red ▲ with no word, inside the
                        * subject text".
                        *
                        * What was wrong with it was not that it was small: it was that a
                        * triangle names no problem. Three different blocking warnings
                        * produced the same mark, so the only way to learn what was wrong
                        * was to open the row — and the mark sat INSIDE the subject, where
                        * a reader scanning for the customer's own words reads it as part
                        * of them.
                        *
                        * The word is `DRAFT_WARNING_LABELS`', the same one the detail
                        * panel shows, and the judgement is still the SERVER's:
                        * `w.blocking` from `drafts/warnings.ts`, which P24 made the one
                        * place three surfaces read. Nothing is decided here.
                        *
                        * `bulkApprovable` stays as the fallback for a draft the server
                        * held back without naming a blocking warning — it is the flag the
                        * checkbox obeys, so the row must not be able to disagree with its
                        * own checkbox.
                        */}
                      {(d.warnings ?? []).filter(w => w.blocking).map(w => (
                        <Badge
                          key={w.code}
                          tone="err"
                          glyph={<AlertTriangle aria-hidden="true" className="h-3 w-3 shrink-0" />}
                          title={tooltipFor(DRAFT_WARNING_LABELS, w.code)}
                          className="mb-1 mr-1 max-w-full"
                        >
                          <span className="min-w-0 truncate">{labelFor(DRAFT_WARNING_LABELS, w.code)}</span>
                        </Badge>
                      ))}
                      {!d.bulkApprovable && (d.warnings ?? []).every(w => !w.blocking) && (
                        <Badge
                          tone="err"
                          glyph={<AlertTriangle aria-hidden="true" className="h-3 w-3 shrink-0" />}
                          title="This draft was held back from bulk approval. Open it to see why."
                          className="mb-1 mr-1"
                        >
                          Read this one
                        </Badge>
                      )}
                      <div className="truncate" title={d.subject}>{d.subject}</div>
                    </td>
                    <td className="px-3 py-2 text-muted">{isoDateTime(d.createdAt)}</td>
                  </tr>
                  {isExpanded(d.id) && (
                    <tr id={`draft-detail-${d.id}`} className="border-t border-gray-100 bg-gray-50">
                      <td colSpan={canEdit ? 7 : 6} className="p-4">
                        <DraftWarnings warnings={d.warnings ?? []} />
                        {d.supersedesDraftId !== null && (
                          <div className="mb-2 rounded border border-gray-300 bg-white px-2.5 py-2 text-xs text-gray-700">
                            <strong>Correction.</strong> This replaces what draft {d.supersedesDraftId} sent to the
                            customer. It goes into the same mail thread — edit the wording and the document below,
                            then approve it like any other reply.
                          </div>
                        )}
                        <div className="mb-2 text-xs text-gray-500">Cc: {d.ccAddresses?.join(', ') || '—'}</div>
                        <div
                          className="rounded border border-gray-200 bg-white p-3 text-sm leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.bodyHtml) }}
                        />
                        <DocumentPanel draft={d} canEdit={canEdit} />
                        {canEdit && <div className="mt-3"><DraftEditor key={`${d.id}-${d.editedBeforeSend}`} draft={d} /></div>}
                        {canEdit && (
                          <div className="mt-3 flex gap-2">
                            <Button
                              disabled={approve.isPending}
                              onClick={() => approve.mutate({ id: d.id }, {
                                // "Approved" and "sent" are different things, and
                                // an approver who believes a customer has the mail
                                // when nothing left the building is the failure
                                // this screen exists to prevent. The send outcome
                                // comes back on the response — report THAT.
                                onSuccess: (result) => {
                                  const send = result.send;
                                  if (send?.status === 'Sent') toast('Approved and sent', 'success');
                                  // P37a: STICKY, and it says what happens next.
                                  // This was a four-second info toast, and four
                                  // seconds is what Katia had to read the only
                                  // notice she ever got that her approval had not
                                  // sent anything (2026-09-03, draft 46). A
                                  // message a reader can miss by looking away is
                                  // not a message; the error-toast rule applies.
                                  else if (send?.status === 'Skipped') {
                                    toast(
                                      `Approved, not sent — ${send.reason} `
                                      + 'The approval is recorded and the mail is still here. The ticket now says so '
                                      + 'and is in Review; the draft is under Failed distributions below, where it '
                                      + 'can be returned to the queue.',
                                      'info',
                                      { sticky: true },
                                    );
                                  } else if (send?.status === 'Failed') toast(`Approved, but the send FAILED — ${send.reason}`, 'error');
                                  else toast('Draft approved', 'success');
                                },
                                onError: () => toast('Failed to approve — it may already have been decided', 'error'),
                              })}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => setRejectTarget(d)}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {(drafts ?? []).length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 7 : 6} className="px-3 py-10 text-center text-sm text-muted">
                    Nothing pending approval.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/*
        * P40: the kit's `Dialog`, not a local `<div>` any more.
        *
        * This was the twelfth modal in the app and the one P36 did not reach, so it
        * had none of the five behaviours the other eleven got: no `role="dialog"`,
        * no `aria-modal`, no accessible name, no Escape, no focus trap, no focus
        * returned to the opener. It also carried the app's only `shadow-xl` against
        * the kit's `shadow-overlay` - three shadow levels where the reference has one.
        *
        * `autoFocus` is gone with it: `Dialog` puts focus in the first EMPTY field,
        * which is this textarea, and does it without fighting the focus trap.
        */}
      {rejectTarget && (
        <Dialog
          title="Reject draft"
          onClose={() => { setRejectTarget(null); setRejectNote(''); }}
          footer={(
            <>
              <DialogCancel onClick={() => { setRejectTarget(null); setRejectNote(''); }} />
              <Button
                variant="destructive"
                size="sm"
                disabled={!rejectNote.trim() || reject.isPending}
                onClick={() => reject.mutate({ id: rejectTarget.id, reviewNote: rejectNote }, {
                  onSuccess: () => { toast('Draft rejected — automatic drafting is paused for this ticket', 'success'); setRejectTarget(null); setRejectNote(''); },
                  onError: () => toast('Failed to reject — it may already have been decided', 'error'),
                })}
              >
                Reject
              </Button>
            </>
          )}
        >
          <p>Why is this being sent back? Regulatory needs a reason.</p>
          {/* P24: rejecting now also stops the agent re-writing the same mail
              five minutes later. Said here because it is the reason the note
              is worth writing carefully — nothing overwrites it. */}
          <p className="mt-1 text-xs text-muted">
            The agent stops drafting for this ticket until somebody composes, corrects a fact or moves it.
          </p>
          <Field label="Reason" className="mt-3">
            {(a) => (
              <Textarea
                {...a}
                rows={3}
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
              />
            )}
          </Field>
        </Dialog>
      )}
    </div>
  );
}

const FAILED_DISTRIBUTIONS_CSV_COLUMNS: CsvColumn<FailedDistributionRow>[] = [
  { header: 'Ticket', value: r => r.ticketNumber },
  { header: 'Draft', value: r => r.draftId },
  { header: 'Status', value: r => r.status },
  { header: 'Reason', value: r => r.reason },
  { header: 'Recipient(s)', value: r => r.toAddresses.join('; ') },
  { header: 'Occurred at', value: r => r.occurredAt },
];

/**
 * Every send the pipeline did not complete (P19, Marcello's report 4) —
 * collapsed by default, since most days it is empty. Two independent sources,
 * both real: a `Failed`/`Rejected` draft, and a draft the recipient-domain
 * allowlist held back (its own `Status` stays `Approved` — nothing went
 * wrong, a policy held).
 *
 * **P24 gives the third case an action.** An `Approved` draft that was never
 * sent used to be a frozen end state: not editable (the editor needs
 * `PendingApproval`), not approvable again, not replaceable — draft 32 to
 * `giovannilippa@hotmail.com` has sat here since 2026-08-31. "Return to the
 * queue" puts it back where it can be edited and approved, under a concurrency
 * guard that refuses the moment a send has actually happened.
 */
function FailedDistributionsPanel({ canEdit, highlightDraftId }: { canEdit: boolean; highlightDraftId?: number | null }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useFailedDistributions();
  const { toast } = useToast();
  const reopen = useReopenDraft();
  const rows = data ?? [];

  /**
   * P37a. A draft linked to from a ticket opens this panel by itself, but only
   * once it is known to be IN it — the rows arrive after the first render, and
   * unfolding an empty box would be a worse answer than leaving it shut.
   */
  const linkedRowPresent = rows.some(r => sameDraft(r.draftId, highlightDraftId));
  useEffect(() => {
    if (linkedRowPresent) setOpen(true);
  }, [linkedRowPresent]);

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 text-sm">
      {/* P40: a disclosure with no ring and no `aria-expanded` - the keyboard could
          reach it and nothing told a reader what it does or whether it is open. */}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={`flex w-full items-center justify-between rounded-ctl text-left ${FOCUS_RING_TIGHT}`}
      >
        <span className="font-medium text-gray-900">
          Failed distributions{!isLoading && ` (${rows.length})`}
        </span>
        <span className="text-xs text-gray-500">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <>
          <p className="mt-1 text-xs text-gray-500">
            Every send that did NOT complete — a Failed or Rejected draft, or one still held back by the recipient
            domain policy pending the pilot's go-live.
          </p>

          {isLoading && <div className="mt-2 text-xs text-muted">Loading…</div>}
          {!isLoading && rows.length === 0 && (
            <div className="mt-2 text-xs text-muted">Nothing failed and nothing was blocked.</div>
          )}

          {!isLoading && rows.length > 0 && (
            <>
              <button
                className="mt-2 rounded-md border px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => downloadCsv('failed-distributions.csv', rows, FAILED_DISTRIBUTIONS_CSV_COLUMNS)}
              >
                Download CSV
              </button>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="pb-1 pr-3">Ticket</th>
                      <th className="pb-1 pr-3">Draft</th>
                      <th className="pb-1 pr-3">Status</th>
                      <th className="pb-1 pr-3">Reason</th>
                      <th className="pb-1 pr-3">To</th>
                      <th className="pb-1 pr-3">When</th>
                      {canEdit && <th className="pb-1 pr-3" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr
                        key={`${r.draftId ?? 'x'}-${i}`}
                        className={`border-t border-gray-100 ${
                          sameDraft(r.draftId, highlightDraftId) ? 'bg-warn-50' : ''}`}
                      >
                        <td className="py-1 pr-3">
                          {r.ticketNumber
                            ? <Link to={`/tickets/${r.ticketNumber}`} className={TEXT_LINK}>{r.ticketNumber}</Link>
                            : '—'}
                        </td>
                        <td className="py-1 pr-3">{r.draftId ?? '—'}</td>
                        <td className="py-1 pr-3" title={`${tooltipFor(DRAFT_STATUS_LABELS, r.status)}

Code: ${r.status}`}>
                          {labelFor(DRAFT_STATUS_LABELS, r.status)}
                        </td>
                        <td className="max-w-xs truncate py-1 pr-3" title={r.reason ?? undefined}>{r.reason ?? '—'}</td>
                        <td className="py-1 pr-3">{r.toAddresses.join(', ') || '—'}</td>
                        <td className="py-1 pr-3">{isoDateTime(r.occurredAt)}</td>
                        {canEdit && (
                          <td className="py-1 pr-3">
                            {/* Only an APPROVED row: a Rejected draft was decided
                                against, and a Failed one needs its cause fixed
                                first — the endpoint refuses both anyway. */}
                            {r.status === 'Approved' && r.draftId !== null && (
                              <button
                                className="rounded-md border px-2 py-0.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                disabled={reopen.isPending}
                                title="Put this draft back in the approval queue so it can be edited and approved again."
                                onClick={() => reopen.mutate(
                                  { id: r.draftId as number, reason: 'Returned to the queue from Failed distributions' },
                                  {
                                    onSuccess: () => toast('Back in the queue — it can be edited and approved again', 'success'),
                                    onError: (err: unknown) => toast(
                                      err instanceof Error ? err.message : 'Could not return it to the queue', 'error',
                                    ),
                                  },
                                )}
                              >
                                Return to the queue
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
